import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  TimePicker,
  message,
  Popconfirm,
  Space,
  Card,
  Tag,
  Row,
  Col,
  Radio,
  InputNumber,
  Tabs,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ClockCircleOutlined, FilePdfOutlined, FileExcelOutlined } from '@ant-design/icons';
import api from '../services/api';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';

const { TextArea } = Input;

const Asistencias = () => {
  const [asistencias, setAsistencias] = useState([]);
  const [personal, setPersonal] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAsistencia, setEditingAsistencia] = useState(null);
  const [form] = Form.useForm();
  const [formIngreso] = Form.useForm();
  const [modalIngresoVisible, setModalIngresoVisible] = useState(false);
  const [personalSeleccionado, setPersonalSeleccionado] = useState(null);
  const [fechaFiltro, setFechaFiltro] = useState(dayjs());
  const [rangoFechas, setRangoFechas] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
  const [personalFiltro, setPersonalFiltro] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submittingIngreso, setSubmittingIngreso] = useState(false);

  useEffect(() => {
    const cargarDatos = async () => {
      await fetchAsistencias();
      await fetchPersonal();
      // fetchUnidades se llamará automáticamente cuando asistencias se actualice
    };
    cargarDatos();
  }, []);

  const fetchAsistencias = async () => {
    setLoading(true);
    try {
      const response = await api.get('/asistencias');
      setAsistencias(response.data);
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPersonal = async () => {
    try {
      const response = await api.get('/personal');
      setPersonal(response.data.filter(p => p.estado === 'activo'));
    } catch (error) {
      console.error('Error al cargar personal');
    }
  };

  const fetchUnidades = async (asistenciasData = asistencias) => {
    try {
      const response = await api.get('/unidades');
      const fechaHoy = dayjs().format('YYYY-MM-DD');

      // Obtener unidades asignadas hoy
      const unidadesAsignadasHoy = asistenciasData
        .filter(a => a.fecha === fechaHoy && a.unidad_id && a.tipo === 'trabajo')
        .map(a => a.unidad_id);

      // Filtrar unidades disponibles y no asignadas
      const unidadesDisponibles = response.data.filter(u =>
        (u.estado === 'disponible' || u.estado === 'en_servicio') &&
        !unidadesAsignadasHoy.includes(u.id)
      );

      setUnidades(unidadesDisponibles);
    } catch (error) {
      console.error('Error al cargar unidades');
    }
  };

  // Actualizar unidades disponibles cuando cambian las asistencias
  useEffect(() => {
    if (asistencias.length > 0 || asistencias.length === 0) {
      fetchUnidades(asistencias);
    }
  }, [asistencias.length]);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const data = {
        ...values,
        // Usar fecha_ingreso como fecha principal si no se proporciona fecha
        fecha: values.fecha_ingreso ? values.fecha_ingreso.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        fecha_ingreso: values.fecha_ingreso ? values.fecha_ingreso.format('YYYY-MM-DD') : null,
        fecha_salida: values.fecha_salida ? values.fecha_salida.format('YYYY-MM-DD') : null,
        hora_ingreso: values.hora_ingreso ? values.hora_ingreso.format('HH:mm:ss') : null,
        hora_salida: values.hora_salida ? values.hora_salida.format('HH:mm:ss') : null,
      };

      if (editingAsistencia) {
        await api.put(`/asistencias/${editingAsistencia.id}`, data);
        message.success('Asistencia actualizada exitosamente');
      } else {
        await api.post('/asistencias', data);
        message.success('Asistencia registrada exitosamente');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingAsistencia(null);
      fetchAsistencias();
    } catch (error) {
      message.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (record) => {
    setEditingAsistencia(record);
    form.setFieldsValue({
      personal_id: record.personal_id?.toString(),
      fecha_ingreso: record.fecha_ingreso ? dayjs(record.fecha_ingreso) : (record.fecha ? dayjs(record.fecha) : dayjs()),
      fecha_salida: record.fecha_salida ? dayjs(record.fecha_salida) : null,
      hora_ingreso: record.hora_ingreso ? dayjs(record.hora_ingreso, 'HH:mm:ss') : null,
      hora_salida: record.hora_salida ? dayjs(record.hora_salida, 'HH:mm:ss') : null,
      tipo: record.tipo || 'trabajo',
      unidad_id: record.unidad_id?.toString(),
      km_inicio_unidad: record.km_inicio_unidad,
      km_fin_unidad: record.km_fin_unidad,
      observaciones: record.observaciones,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/asistencias/${id}`);
      message.success('Asistencia eliminada exitosamente');
      fetchAsistencias();
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleAbrirModalIngreso = (personalId) => {
    const persona = personal.find(p => p.id === personalId);
    setPersonalSeleccionado(persona);
    formIngreso.resetFields();
    formIngreso.setFieldsValue({
      personal_id: personalId
    });
    setModalIngresoVisible(true);
  };

  const handleMarcarIngreso = async (values) => {
    setSubmittingIngreso(true);
    try {
      const fecha = dayjs().format('YYYY-MM-DD');
      const hora = dayjs().format('HH:mm:ss');
      const personalId = values.personal_id;

      // Verificar si ya existe asistencia activa (sin salida) para este personal
      const existing = asistencias.find(
        a => a.personal_id == personalId &&
          a.tipo === 'trabajo' &&
          a.hora_ingreso &&
          (!a.hora_salida || !a.fecha_salida)
      );

      const data = {
        hora_ingreso: hora,
        fecha_ingreso: fecha,
        tipo: 'trabajo',
        unidad_id: values.unidad_id || null,
        km_inicio_unidad: values.km_inicio_unidad || null
      };

      if (existing) {
        // Actualizar hora de ingreso de la asistencia existente
        await api.put(`/asistencias/${existing.id}`, data);
        message.success('Hora de ingreso actualizada');
      } else {
        // Crear nueva asistencia
        await api.post('/asistencias', {
          personal_id: personalId,
          fecha: fecha,
          ...data
        });
        message.success('Hora de ingreso registrada');
      }
      setModalIngresoVisible(false);
      formIngreso.resetFields();
      setPersonalSeleccionado(null);
      fetchAsistencias();
    } catch (error) {
      message.error(error.message);
    } finally {
      setSubmittingIngreso(false);
    }
  };

  const handleMarcarSalida = async (personalId) => {
    try {
      const fechaHoy = dayjs().format('YYYY-MM-DD');
      const hora = dayjs().format('HH:mm:ss');

      // Buscar asistencia activa (con hora_ingreso pero sin hora_salida) sin importar la fecha
      const existing = asistencias.find(
        a => a.personal_id == personalId &&
          a.tipo === 'trabajo' &&
          a.hora_ingreso &&
          (!a.hora_salida || !a.fecha_salida)
      );

      if (existing) {
        await api.put(`/asistencias/${existing.id}`, {
          hora_salida: hora,
          fecha_salida: fechaHoy
        });
        message.success('Hora de salida registrada. Horas trabajadas calculadas.');
      } else {
        message.warning('Primero debe registrar la hora de ingreso');
      }
      fetchAsistencias();
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleMarcarDescanso = async (personalId) => {
    try {
      const fecha = dayjs().format('YYYY-MM-DD');

      // Verificar si ya existe asistencia para hoy
      const existing = asistencias.find(
        a => a.personal_id === personalId && a.fecha === fecha
      );

      if (existing) {
        await api.put(`/asistencias/${existing.id}`, {
          tipo: 'descanso',
          hora_ingreso: null,
          hora_salida: null,
          horas_trabajadas: null
        });
        message.success('Descanso registrado');
      } else {
        await api.post('/asistencias', {
          personal_id: personalId,
          fecha: fecha,
          tipo: 'descanso'
        });
        message.success('Descanso registrado');
      }
      fetchAsistencias();
    } catch (error) {
      message.error(error.message);
    }
  };

  const calcularHorasTrabajadas = (horaIngreso, horaSalida, fechaIngreso, fechaSalida) => {
    if (!horaIngreso || !horaSalida) return '-';

    // Si hay fechas diferentes, usarlas; sino usar la fecha actual
    const fechaIngresoCalc = fechaIngreso || dayjs().format('YYYY-MM-DD');
    const fechaSalidaCalc = fechaSalida || dayjs().format('YYYY-MM-DD');

    const ingreso = dayjs(`${fechaIngresoCalc} ${horaIngreso}`, 'YYYY-MM-DD HH:mm:ss');
    const salida = dayjs(`${fechaSalidaCalc} ${horaSalida}`, 'YYYY-MM-DD HH:mm:ss');
    const diff = salida.diff(ingreso, 'hour', true);

    const horas = Math.floor(diff);
    const minutos = Math.floor((diff - horas) * 60);

    return `${horas}h ${minutos}m`;
  };

  // Filtrar asistencias por rango de fechas
  const asistenciasFiltradas = asistencias.filter(a => {
    // Filtro por fecha (Intersección de rangos)
    if (rangoFechas && rangoFechas[0] && rangoFechas[1]) {
      const rangoInicio = rangoFechas[0].startOf('day');
      const rangoFin = rangoFechas[1].endOf('day');

      const fechaIngreso = dayjs(a.fecha_ingreso || a.fecha).startOf('day');
      // Si no tiene fecha de salida pero es un registro histórico (ya filtramos activos abajo),
      // asumimos que terminó el mismo día o usamos la fecha base.
      // Para mayor robustez, usamos fecha_ingreso como fallback si fecha_salida es nula.
      let fechaSalida = a.fecha_salida ? dayjs(a.fecha_salida) : (a.hora_salida ? dayjs(a.fecha) : fechaIngreso);
      fechaSalida = fechaSalida.endOf('day');

      // Lógica de intersección: (Inicio <= RangoFin) Y (Fin >= RangoInicio)
      if (fechaIngreso.isAfter(rangoFin) || fechaSalida.isBefore(rangoInicio)) {
        return false;
      }
    }

    // Filtro por personal
    if (personalFiltro && a.personal_id !== personalFiltro) {
      return false;
    }

    // Filtro: Solo mostrar asistencias con salida marcada (completadas)
    // Se excluyen los registros que no tienen fecha de salida ni hora de salida
    if (!a.hora_salida && !a.fecha_salida) {
      return false;
    }

    return true;
  });

  const handleExportarPDF = async () => {
    if (!rangoFechas || !rangoFechas[0] || !rangoFechas[1]) {
      message.warning('Selecciona un rango de fechas');
      return;
    }

    try {
      message.loading({ content: 'Generando PDF...', key: 'export' });

      // Crear elemento temporal para el reporte
      const reportDiv = document.createElement('div');
      reportDiv.style.width = '210mm';
      reportDiv.style.padding = '20mm';
      reportDiv.style.backgroundColor = 'white';
      reportDiv.style.fontFamily = 'Arial, sans-serif';

      const fechaInicio = rangoFechas[0].format('DD/MM/YYYY');
      const fechaFin = rangoFechas[1].format('DD/MM/YYYY');

      let html = `
        <div style="text-align: center; margin-bottom: 20px;">
          <h1 style="margin: 0; font-size: 24px;">Reporte de Asistencias</h1>
          <p style="margin: 5px 0; font-size: 14px; color: #666;">
            Del ${fechaInicio} al ${fechaFin}
          </p>
        </div>
        <table style="width: 100%; border-collapse: collapse; font-size: 10px;">
          <thead>
            <tr style="background-color: #f0f0f0;">
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Personal</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Fecha Ingreso</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Fecha Salida</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Tipo</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Hora Ingreso</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Hora Salida</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Horas Trabajadas</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Unidad</th>
            </tr>
          </thead>
          <tbody>
      `;

      asistenciasFiltradas.forEach((a, index) => {
        // Calcular horas trabajadas usando la misma lógica que en la tabla
        let horasTrabajadas = '-';
        if (a.tipo !== 'descanso') {
          if (a.horas_trabajadas) {
            const horas = Math.floor(a.horas_trabajadas);
            const minutos = Math.floor((a.horas_trabajadas - horas) * 60);
            horasTrabajadas = `${horas}h ${minutos}m`;
          } else if (a.hora_ingreso && a.hora_salida) {
            const fechaIngreso = a.fecha_ingreso || a.fecha;
            const fechaSalida = a.fecha_salida || (a.hora_salida ? a.fecha : null);
            horasTrabajadas = calcularHorasTrabajadas(
              a.hora_ingreso,
              a.hora_salida,
              fechaIngreso,
              fechaSalida
            );
          }
        }

        // Obtener fechas de ingreso y salida
        const fechaIngreso = a.fecha_ingreso || a.fecha;
        const fechaSalida = a.fecha_salida || (a.hora_salida ? a.fecha : null);

        html += `
          <tr style="background-color: ${index % 2 === 0 ? 'white' : '#f9f9f9'};">
            <td style="border: 1px solid #ddd; padding: 6px;">${a.personal_nombre || '-'}</td>
            <td style="border: 1px solid #ddd; padding: 6px;">${fechaIngreso ? dayjs(fechaIngreso).format('DD/MM/YYYY') : '-'}</td>
            <td style="border: 1px solid #ddd; padding: 6px;">${fechaSalida ? dayjs(fechaSalida).format('DD/MM/YYYY') : '-'}</td>
            <td style="border: 1px solid #ddd; padding: 6px;">${a.tipo === 'trabajo' ? 'Trabajo' : 'Descanso'}</td>
            <td style="border: 1px solid #ddd; padding: 6px;">${a.hora_ingreso ? a.hora_ingreso.substring(0, 5) : '-'}</td>
            <td style="border: 1px solid #ddd; padding: 6px;">${a.hora_salida ? a.hora_salida.substring(0, 5) : '-'}</td>
            <td style="border: 1px solid #ddd; padding: 6px;">${horasTrabajadas}</td>
            <td style="border: 1px solid #ddd; padding: 6px;">${a.unidad_placa || '-'}</td>
          </tr>
        `;
      });

      html += `
          </tbody>
        </table>
        <div style="margin-top: 20px; text-align: right; font-size: 10px; color: #666;">
          <p>Total de registros: ${asistenciasFiltradas.length}</p>
        </div>
      `;

      reportDiv.innerHTML = html;
      document.body.appendChild(reportDiv);

      const canvas = await html2canvas(reportDiv, {
        scale: 2,
        useCORS: true,
        logging: false
      });

      document.body.removeChild(reportDiv);

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const nombreArchivo = `Reporte_Asistencias_${fechaInicio.replace(/\//g, '-')}_${fechaFin.replace(/\//g, '-')}.pdf`;
      pdf.save(nombreArchivo);

      message.success({ content: 'PDF generado exitosamente', key: 'export' });
    } catch (error) {
      console.error('Error al generar PDF:', error);
      message.error({ content: 'Error al generar PDF', key: 'export' });
    }
  };

  const handleExportarExcel = () => {
    if (!rangoFechas || !rangoFechas[0] || !rangoFechas[1]) {
      message.warning('Selecciona un rango de fechas');
      return;
    }

    try {
      const fechaInicio = rangoFechas[0].format('DD/MM/YYYY');
      const fechaFin = rangoFechas[1].format('DD/MM/YYYY');

      // Preparar datos para Excel
      const datosExcel = asistenciasFiltradas.map(a => {
        // Calcular horas trabajadas usando la misma lógica que en la tabla
        let horasTrabajadas = '-';
        if (a.tipo !== 'descanso') {
          if (a.horas_trabajadas) {
            const horas = Math.floor(a.horas_trabajadas);
            const minutos = Math.floor((a.horas_trabajadas - horas) * 60);
            horasTrabajadas = `${horas}h ${minutos}m`;
          } else if (a.hora_ingreso && a.hora_salida) {
            const fechaIngreso = a.fecha_ingreso || a.fecha;
            const fechaSalida = a.fecha_salida || (a.hora_salida ? a.fecha : null);
            horasTrabajadas = calcularHorasTrabajadas(
              a.hora_ingreso,
              a.hora_salida,
              fechaIngreso,
              fechaSalida
            );
          }
        }

        // Obtener fechas de ingreso y salida
        const fechaIngreso = a.fecha_ingreso || a.fecha;
        const fechaSalida = a.fecha_salida || (a.hora_salida ? a.fecha : null);

        return {
          'Personal': a.personal_nombre || '-',
          'Documento': a.personal_documento || '-',
          'Cargo': a.personal_cargo || '-',
          'Fecha Ingreso': fechaIngreso ? dayjs(fechaIngreso).format('DD/MM/YYYY') : '-',
          'Fecha Salida': fechaSalida ? dayjs(fechaSalida).format('DD/MM/YYYY') : '-',
          'Tipo': a.tipo === 'trabajo' ? 'Trabajo' : 'Descanso',
          'Hora Ingreso': a.hora_ingreso ? a.hora_ingreso.substring(0, 5) : '-',
          'Hora Salida': a.hora_salida ? a.hora_salida.substring(0, 5) : '-',
          'Horas Trabajadas': horasTrabajadas,
          'Unidad Placa': a.unidad_placa || '-',
          'Unidad Tipo': a.unidad_tipo || '-',
          'Km. Inicio Unidad': a.km_inicio_unidad ? parseFloat(a.km_inicio_unidad).toFixed(2) : '-',
          'Km. Fin Unidad': a.km_fin_unidad ? parseFloat(a.km_fin_unidad).toFixed(2) : '-',
          'Observaciones': a.observaciones || '-'
        };
      });

      // Crear workbook y worksheet
      const ws = XLSX.utils.json_to_sheet(datosExcel);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Asistencias');

      // Ajustar ancho de columnas
      const colWidths = [
        { wch: 25 }, // Personal
        { wch: 15 }, // Documento
        { wch: 20 }, // Cargo
        { wch: 12 }, // Fecha Ingreso
        { wch: 12 }, // Fecha Salida
        { wch: 10 }, // Tipo
        { wch: 12 }, // Hora Ingreso
        { wch: 12 }, // Hora Salida
        { wch: 15 }, // Horas Trabajadas
        { wch: 15 }, // Unidad Placa
        { wch: 20 }, // Unidad Tipo
        { wch: 15 }, // Km. Inicio
        { wch: 30 }  // Observaciones
      ];
      ws['!cols'] = colWidths;

      // Generar archivo
      const nombreArchivo = `Reporte_Asistencias_${fechaInicio.replace(/\//g, '-')}_${fechaFin.replace(/\//g, '-')}.xlsx`;
      XLSX.writeFile(wb, nombreArchivo);

      message.success('Excel generado exitosamente');
    } catch (error) {
      console.error('Error al generar Excel:', error);
      message.error(error.message);
    }
  };

  const columns = [
    {
      title: 'Personal',
      dataIndex: 'personal_nombre',
      key: 'personal_nombre',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#999' }}>
            {record.personal_documento} - {record.personal_cargo}
          </div>
        </div>
      ),
    },
    {
      title: 'Fecha Ingreso',
      dataIndex: 'fecha_ingreso',
      key: 'fecha_ingreso',
      render: (fecha, record) => {
        const fechaMostrar = fecha || record.fecha;
        return fechaMostrar ? dayjs(fechaMostrar).format('DD/MM/YYYY') : '-';
      },
    },
    {
      title: 'Fecha Salida',
      dataIndex: 'fecha_salida',
      key: 'fecha_salida',
      render: (fecha, record) => {
        const fechaMostrar = fecha || (record.hora_salida ? record.fecha : null);
        return fechaMostrar ? dayjs(fechaMostrar).format('DD/MM/YYYY') : '-';
      },
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      render: (tipo) => (
        <Tag color={tipo === 'trabajo' ? 'green' : 'orange'}>
          {tipo === 'trabajo' ? 'Trabajo' : 'Descanso'}
        </Tag>
      ),
    },
    {
      title: 'Hora Ingreso',
      dataIndex: 'hora_ingreso',
      key: 'hora_ingreso',
      render: (hora) => hora ? hora.substring(0, 5) : '-',
    },
    {
      title: 'Hora Salida',
      dataIndex: 'hora_salida',
      key: 'hora_salida',
      render: (hora) => hora ? hora.substring(0, 5) : '-',
    },
    {
      title: 'Horas Trabajadas',
      key: 'horas_trabajadas',
      render: (_, record) => {
        if (record.tipo === 'descanso') return '-';
        if (record.horas_trabajadas) {
          const horas = Math.floor(record.horas_trabajadas);
          const minutos = Math.floor((record.horas_trabajadas - horas) * 60);
          return `${horas}h ${minutos}m`;
        }
        return calcularHorasTrabajadas(
          record.hora_ingreso,
          record.hora_salida,
          record.fecha_ingreso || record.fecha,
          record.fecha_salida || (record.hora_salida ? record.fecha : null)
        );
      },
    },
    {
      title: 'Unidad Asignada',
      key: 'unidad',
      render: (_, record) => {
        if (record.tipo === 'descanso' || !record.unidad_placa) return '-';
        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>{record.unidad_placa}</div>
            {record.unidad_tipo && (
              <div style={{ fontSize: '12px', color: '#999' }}>
                {record.unidad_tipo}
              </div>
            )}
            {record.km_inicio_unidad && (
              <div style={{ fontSize: '12px', color: '#666' }}>
                Km. inicio: {parseFloat(record.km_inicio_unidad).toFixed(2)}
              </div>
            )}
            {record.km_fin_unidad && (
              <div style={{ fontSize: '12px', color: '#666' }}>
                Km. fin: {parseFloat(record.km_fin_unidad).toFixed(2)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Estás seguro de eliminar esta asistencia?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button type="primary" danger icon={<DeleteOutlined />}>
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const hoy = dayjs().format('YYYY-MM-DD');
  const asistenciasHoy = asistencias.filter(a => a.fecha === hoy);

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <h2 style={{ margin: 0 }}>Asistencias del Personal</h2>
          <Space>

            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingAsistencia(null);
                form.resetFields();
                form.setFieldsValue({
                  fecha_ingreso: dayjs(),
                  tipo: 'trabajo'
                });
                setModalVisible(true);
              }}
            >
              Nueva Asistencia
            </Button>
          </Space>
        </div>

        <Tabs defaultActiveKey="1">
          <Tabs.TabPane tab="Control de Asistencia" key="1">
            <Card
              title={`Marcado de Asistencia - ${dayjs().format('DD/MM/YYYY')}`}
              style={{ marginBottom: 24 }}
            >
              <Form layout="inline" style={{ marginBottom: 16 }}>
                <Form.Item label="Seleccionar Personal" style={{ marginBottom: 16, width: '100%', maxWidth: 400 }}>
                  <Select
                    placeholder="Selecciona el personal"
                    style={{ width: '100%' }}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={(value) => {
                      setPersonalSeleccionado(personal.find(p => p.id === value));
                    }}
                  >
                    {personal.map((p) => {
                      // Buscar si tiene asistencia hoy
                      const asistenciaHoy = asistenciasHoy.find(a => a.personal_id === p.id);
                      const esDescanso = asistenciaHoy?.tipo === 'descanso';

                      // Buscar si tiene una asistencia activa de trabajo (sin salida), de cualquier fecha
                      const asistenciaActiva = asistencias.find(a =>
                        a.personal_id == p.id &&
                        a.tipo === 'trabajo' &&
                        a.hora_ingreso &&
                        (!a.hora_salida || !a.fecha_salida)
                      );

                      let estado = '';
                      if (esDescanso) {
                        estado = ' - [Descanso]';
                      } else if (asistenciaHoy && asistenciaHoy.hora_salida) {
                        estado = ' - [Completado]';
                      } else if (asistenciaActiva) {
                        // Si tiene asistencia activa (sea de hoy o antes), mostrar En trabajo
                        estado = ' - [En trabajo]';
                      }

                      return (
                        <Select.Option key={p.id} value={p.id}>
                          {p.nombre_completo} - {p.cargo}{estado}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              </Form>

              {personalSeleccionado && (
                <Card
                  size="small"
                  style={{ marginBottom: 16, backgroundColor: '#f5f5f5' }}
                >
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: 4 }}>
                      {personalSeleccionado.nombre_completo}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {personalSeleccionado.documento} - {personalSeleccionado.cargo}
                    </div>
                  </div>

                  {(() => {
                    const asistenciaHoy = asistenciasHoy.find(a => a.personal_id === personalSeleccionado.id);
                    const esDescanso = asistenciaHoy?.tipo === 'descanso';

                    // Verificar si tiene asistencia activa en trabajo (sin importar la fecha)
                    const asistenciaActivaTrabajo = asistencias.find(a =>
                      a.personal_id == personalSeleccionado.id &&
                      a.tipo === 'trabajo' &&
                      a.hora_ingreso &&
                      (!a.hora_salida || !a.fecha_salida)
                    );

                    // Usar la asistencia activa si existe, sino usar la de hoy
                    const asistenciaMostrar = asistenciaActivaTrabajo || asistenciaHoy;
                    const tieneIngreso = asistenciaMostrar?.hora_ingreso;
                    const tieneSalida = asistenciaMostrar?.hora_salida;

                    return (
                      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        {esDescanso ? (
                          <Tag color="orange" style={{ fontSize: '14px', padding: '8px 16px' }}>
                            Descanso registrado para hoy
                          </Tag>
                        ) : asistenciaMostrar ? (
                          <div>
                            {asistenciaActivaTrabajo && asistenciaActivaTrabajo.fecha !== hoy && (
                              <div style={{ marginBottom: 8, color: '#ff9800', fontWeight: 'bold', fontSize: '12px' }}>
                                ⚠️ Asistencia activa desde {dayjs(asistenciaActivaTrabajo.fecha).format('DD/MM/YYYY')}
                              </div>
                            )}
                            <div style={{ marginBottom: 8 }}>
                              <ClockCircleOutlined style={{ marginRight: 8 }} />
                              <strong>Ingreso:</strong> {asistenciaMostrar.hora_ingreso ? asistenciaMostrar.hora_ingreso.substring(0, 5) : '-'}
                              {asistenciaMostrar.fecha && (
                                <span style={{ fontSize: '12px', color: '#666', marginLeft: 8 }}>
                                  ({dayjs(asistenciaMostrar.fecha).format('DD/MM/YYYY')})
                                </span>
                              )}
                            </div>
                            {asistenciaMostrar.hora_salida && (
                              <div style={{ marginBottom: 8 }}>
                                <ClockCircleOutlined style={{ marginRight: 8 }} />
                                <strong>Salida:</strong> {asistenciaMostrar.hora_salida.substring(0, 5)}
                              </div>
                            )}
                            {asistenciaMostrar.horas_trabajadas && (
                              <div style={{ marginBottom: 8, color: '#1890ff', fontWeight: 'bold' }}>
                                Horas trabajadas: {Math.floor(asistenciaMostrar.horas_trabajadas)}h {Math.floor((asistenciaMostrar.horas_trabajadas - Math.floor(asistenciaMostrar.horas_trabajadas)) * 60)}m
                              </div>
                            )}
                            {asistenciaMostrar.unidad_placa && (
                              <div style={{ marginBottom: 8, fontSize: '12px', color: '#666' }}>
                                Unidad asignada: {asistenciaMostrar.unidad_placa} - {asistenciaMostrar.unidad_tipo}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div style={{ color: '#999', marginBottom: 8 }}>
                            Sin registro de asistencia para hoy
                          </div>
                        )}

                        <Space size="middle">
                          {!esDescanso && !tieneIngreso && !asistenciaActivaTrabajo && (
                            <Button
                              type="primary"
                              size="large"
                              icon={<ClockCircleOutlined />}
                              onClick={() => handleAbrirModalIngreso(personalSeleccionado.id)}
                            >
                              Marcar Ingreso
                            </Button>
                          )}
                          {!esDescanso && tieneIngreso && !tieneSalida && (
                            <Button
                              type="default"
                              size="large"
                              icon={<ClockCircleOutlined />}
                              onClick={() => handleMarcarSalida(personalSeleccionado.id)}
                            >
                              Marcar Salida
                            </Button>
                          )}
                          {(() => {
                            // Verificar si tiene asistencia activa en trabajo (sin importar la fecha)
                            const asistenciaActivaTrabajo = asistencias.find(a =>
                              a.personal_id == personalSeleccionado.id &&
                              a.tipo === 'trabajo' &&
                              a.hora_ingreso &&
                              (!a.hora_salida || !a.fecha_salida)
                            );

                            // Solo mostrar botón de descanso si no hay asistencia activa en trabajo
                            if (!asistenciaHoy && !asistenciaActivaTrabajo) {
                              return (
                                <Button
                                  size="large"
                                  onClick={() => handleMarcarDescanso(personalSeleccionado.id)}
                                >
                                  Marcar Descanso
                                </Button>
                              );
                            }
                            return null;
                          })()}
                        </Space>
                      </Space>
                    );
                  })()}
                </Card>
              )}
            </Card>

            {/* TABLA DE ASISTENCIAS ACTIVAS (TRABAJO EN CURSO) */}
            <Card title="Personal en Trabajo Activo (Turnos Abiertos)" style={{ marginTop: 24, border: '1px solid #1890ff' }} headStyle={{ backgroundColor: '#e6f7ff', color: '#1890ff' }}>
              <Table
                dataSource={asistencias.filter(a =>
                  a.tipo === 'trabajo' &&
                  a.hora_ingreso &&
                  (!a.hora_salida || !a.fecha_salida)
                )}
                rowKey="id"
                pagination={false}
                columns={[
                  {
                    title: 'Personal',
                    dataIndex: 'personal_nombre',
                    key: 'personal_nombre',
                    render: (text, record) => (
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{text}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{record.personal_cargo}</div>
                      </div>
                    ),
                  },
                  {
                    title: 'Fecha Ingreso',
                    dataIndex: 'fecha_ingreso',
                    key: 'fecha_ingreso',
                    render: (fecha, record) => {
                      const f = fecha || record.fecha;
                      return f ? dayjs(f).format('DD/MM/YYYY') : '-';
                    }
                  },
                  {
                    title: 'Hora Ingreso',
                    dataIndex: 'hora_ingreso',
                    key: 'hora_ingreso',
                    render: (hora) => <Tag color="blue">{hora ? hora.substring(0, 5) : '-'}</Tag>
                  },
                  {
                    title: 'Unidad',
                    key: 'unidad',
                    render: (_, record) => record.unidad_placa ? (
                      <Tag>{record.unidad_placa}</Tag>
                    ) : '-'
                  },
                  {
                    title: 'Acciones',
                    key: 'acciones',
                    render: (_, record) => (
                      <Button
                        type="primary"
                        danger
                        size="small"
                        icon={<ClockCircleOutlined />}
                        onClick={() => handleMarcarSalida(record.personal_id)}
                      >
                        Marcar Salida
                      </Button>
                    )
                  }
                ]}
              />
            </Card>
          </Tabs.TabPane>

          <Tabs.TabPane tab="Historial de Asistencias" key="2">
            <Card>
              <div style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                <Select
                  placeholder="Filtrar por personal"
                  style={{ width: 250 }}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={(value) => setPersonalFiltro(value)}
                  value={personalFiltro}
                >
                  {personal.map((p) => (
                    <Select.Option key={p.id} value={p.id}>
                      {p.nombre_completo}
                    </Select.Option>
                  ))}
                </Select>

                <DatePicker.RangePicker
                  value={rangoFechas}
                  onChange={(dates) => setRangoFechas(dates)}
                  format="DD/MM/YYYY"
                  style={{ width: 300 }}
                />
                <Button
                  type="default"
                  icon={<FilePdfOutlined />}
                  onClick={handleExportarPDF}
                >
                  Exportar PDF
                </Button>
                <Button
                  type="default"
                  icon={<FileExcelOutlined />}
                  onClick={handleExportarExcel}
                >
                  Exportar Excel
                </Button>
              </div>

              <Table
                columns={columns}
                dataSource={asistenciasFiltradas}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
              />
            </Card>
          </Tabs.TabPane>
        </Tabs>
      </Card>

      <Modal
        title={editingAsistencia ? 'Editar Asistencia' : 'Nueva Asistencia'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingAsistencia(null);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="personal_id"
            label="Personal"
            rules={[{ required: true, message: 'Selecciona el personal' }]}
          >
            <Select placeholder="Selecciona el personal" disabled={!!editingAsistencia}>
              {personal.map((p) => (
                <Select.Option key={p.id} value={p.id}>
                  {p.nombre_completo} - {p.cargo}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="tipo"
            label="Tipo"
            rules={[{ required: true, message: 'Selecciona el tipo' }]}
            initialValue="trabajo"
          >
            <Radio.Group>
              <Radio value="trabajo">Trabajo</Radio>
              <Radio value="descanso">Descanso</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.tipo !== currentValues.tipo}>
            {() => {
              const tipo = form.getFieldValue('tipo');
              if (tipo === 'trabajo') {
                return (
                  <>
                    <Form.Item name="fecha_ingreso" label="Fecha de Ingreso">
                      <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                    </Form.Item>
                    <Form.Item name="hora_ingreso" label="Hora de Ingreso">
                      <TimePicker style={{ width: '100%' }} format="HH:mm" />
                    </Form.Item>
                    <Form.Item name="fecha_salida" label="Fecha de Salida">
                      <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                    </Form.Item>
                    <Form.Item name="hora_salida" label="Hora de Salida">
                      <TimePicker style={{ width: '100%' }} format="HH:mm" />
                    </Form.Item>
                    <Form.Item name="unidad_id" label="Unidad Asignada">
                      <Select
                        placeholder="Selecciona una unidad (opcional)"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        {unidades.map((u) => (
                          <Select.Option key={u.id} value={u.id}>
                            {u.placa} - {u.tipo_grua}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item
                      name="km_inicio_unidad"
                      label="Kilometraje Inicial de la Unidad"
                      dependencies={['unidad_id']}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        placeholder="Ingresa el kilometraje inicial"
                        min={0}
                        precision={2}
                      />
                    </Form.Item>
                    <Form.Item
                      name="km_fin_unidad"
                      label="Kilometraje Final de la Unidad"
                      dependencies={['unidad_id']}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        placeholder="Ingresa el kilometraje final (opcional)"
                        min={0}
                        precision={2}
                      />
                    </Form.Item>
                  </>
                );
              }
              return null;
            }}
          </Form.Item>

          <Form.Item name="observaciones" label="Observaciones">
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting} disabled={submitting}>
                {editingAsistencia ? 'Actualizar' : 'Crear'}
              </Button>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                  setEditingAsistencia(null);
                }}
              >
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal para marcar ingreso con unidad */}
      <Modal
        title={`Marcar Ingreso - ${personalSeleccionado?.nombre_completo || ''}`}
        open={modalIngresoVisible}
        onCancel={() => {
          setModalIngresoVisible(false);
          formIngreso.resetFields();
          setPersonalSeleccionado(null);
        }}
        footer={null}
        width={500}
      >
        <Form
          form={formIngreso}
          layout="vertical"
          onFinish={handleMarcarIngreso}
        >
          <Form.Item
            name="personal_id"
            hidden
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="unidad_id"
            label="Unidad Asignada (Opcional)"
            tooltip="Solo se muestran unidades disponibles y no asignadas hoy"
          >
            <Select
              placeholder="Selecciona una unidad"
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
              onChange={(value) => {
                if (value) {
                  const unidadSeleccionada = unidades.find(u => u.id === value);
                  if (unidadSeleccionada && unidadSeleccionada.km_actual) {
                    formIngreso.setFieldsValue({
                      km_inicio_unidad: parseFloat(unidadSeleccionada.km_actual)
                    });
                  }
                } else {
                  formIngreso.setFieldsValue({
                    km_inicio_unidad: null
                  });
                }
              }}
            >
              {unidades.map((u) => (
                <Select.Option key={u.id} value={u.id}>
                  {u.placa} - {u.tipo_grua} {u.km_actual ? `(Km: ${parseFloat(u.km_actual).toFixed(2)})` : ''}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="km_inicio_unidad"
            label="Kilometraje Inicial de la Unidad"
            dependencies={['unidad_id']}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const unidadId = getFieldValue('unidad_id');
                  if (unidadId && (!value || value === '')) {
                    return Promise.reject(new Error('Ingresa el kilometraje inicial de la unidad'));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Ingresa el kilometraje inicial"
              min={0}
              precision={2}
            />
          </Form.Item>

          <Form.Item
            name="km_fin_unidad"
            label="Kilometraje Final de la Unidad"
            dependencies={['unidad_id']}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Ingresa el kilometraje final (opcional)"
              min={0}
              precision={2}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submittingIngreso} disabled={submittingIngreso}>
                Registrar Ingreso
              </Button>
              <Button
                onClick={() => {
                  setModalIngresoVisible(false);
                  formIngreso.resetFields();
                  setPersonalSeleccionado(null);
                }}
              >
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Asistencias;

